import React, { useEffect, useState } from 'react';
import { Divider, Menu, MenuItem, Stack, IconButton } from '@mui/material';
import { Cached, Print, Redo, Save, Undo, InfoOutlined, MoreVert } from '@mui/icons-material';

import { ReduxUpdater } from '../../../pdf/ReduxUpdater';
import {
    eplassSliceAutoSave,
    eplassSliceViewMode,
    eplassSliceUndoRedoRefresh,
    eplassSliceSavingChanges,
    eplassSliceRibbonRefresh,
    eplassSliceDocumentSaved,
} from '../../../Redux/EplassSlice';
import { useSelector } from 'react-redux';

import { saveDocument } from '../../../pdf/stateHandler/Helper';

import styles from './HeaderMenu.module.scss';
import { GlobalVariables } from '../../../pdf/GlobalVariables';
import i18next from 'i18next';
import { PdfViewerViewModeFlag } from '../../../pdf/Enums';
import HeaderActionItem from './Components/HeaderActionItem';
import { ApryseSDK } from '../../../pdf/ApryseSDK';
import { CustomHistoryManager } from '../../../pdf/CustomHistoryManager';
import ViewModeChip from './Components/ViewModeChip/ViewModeChip';

/** Interface for the header menu props. */
interface IHeaderMenuProps {
    isTablet: boolean;
}

export default ({ isTablet }: IHeaderMenuProps) => {
    const autosave = useSelector(eplassSliceAutoSave);
    const isDocumentSaving = useSelector(eplassSliceSavingChanges);
    const isDocumentSaved = useSelector(eplassSliceDocumentSaved);
    const viewMode = useSelector(eplassSliceViewMode);
    const undoRedoRefresh = useSelector(eplassSliceUndoRedoRefresh);
    const refreshRibbon = useSelector(eplassSliceRibbonRefresh);

    const [canUndo, setCanUndo] = useState<boolean>(false);
    const [canRedo, setCanRedo] = useState<boolean>(false);

    const refreshUndoRedo = () => {
        const newCanUndo =
            CustomHistoryManager.canUndo() ||
            //ApryseSDK.annotationHistoryManager?.canUndo() || --REASON: provides wrong data
            ApryseSDK.contentHistoryManager?.canUndo();

        const newCanRedo =
            CustomHistoryManager.canRedo() ||
            ApryseSDK.annotationHistoryManager?.canRedo() ||
            ApryseSDK.contentHistoryManager?.canRedo();

        setCanUndo(newCanUndo);
        setCanRedo(newCanRedo);
    };

    useEffect(() => {
        refreshUndoRedo();
    }, [undoRedoRefresh]);

    const [anchorHeaderMenu, setAnchorHeaderMenu] = React.useState<null | HTMLElement>(null);

    /**
     * Changes header menu state in order to open header menu.
     */
    const handleOpenHeaderMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorHeaderMenu(event.currentTarget);
    };

    /**
     * Changes header menu state in order to close header menu.
     */
    const handleCloseHeaderMenu = () => {
        setAnchorHeaderMenu(null);
    };

    React.useMemo(() => {
        if (viewMode === PdfViewerViewModeFlag.Annotation) {
            if (autosave) {
                // ToDO:
                // if (GlobalVariables.saveDocumentInterval !== null) {
                //     clearInterval(GlobalVariables.saveDocumentInterval);
                // }
                // saveDocument();
                // // Every 10 minutes
                // GlobalVariables.saveDocumentInterval = setInterval(() => {
                //     saveDocument();
                // }, GlobalVariables.saveDocumentIntervalLength);
                return;
            }
        }

        // ToDO: // Disable timer if it is active
        // if (GlobalVariables.saveDocumentInterval !== null) {
        //     clearInterval(GlobalVariables.saveDocumentInterval);
        //     GlobalVariables.saveDocumentInterval = null;
        // }
    }, [autosave, viewMode]);

    /*
    const autosaveSwitch = React.useMemo(() => {
        if (viewMode === PdfViewerViewModeFlag.Annotation) {
            return (
                <WhiteSwitch
                    onChange={() => {
                        ReduxUpdater.toggleUserAutosave();
                    }}
                    defaultChecked={autosave}
                />
            );
        }
        return <></>;
    }, [autosave, viewMode]);
    */
    /** Action items for desktop and tablet mode. */
    const commonMenuItems = React.useMemo(
        () => [
            <HeaderActionItem
                key="save"
                disabled={isDocumentSaving || isDocumentSaved}
                isTablet={isTablet}
                title={i18next.t('header.save')}
                onClick={() => saveDocument()}
                onClose={handleCloseHeaderMenu}
                icon={
                    isDocumentSaving ? (
                        <Cached
                            sx={{
                                animation: 'spin 2s linear infinite',
                                color: 'white',
                                '@keyframes spin': {
                                    '0%': {
                                        transform: 'rotate(360deg)',
                                    },
                                    '100%': {
                                        transform: 'rotate(0deg)',
                                    },
                                },
                            }}
                        />
                    ) : (
                        <Save />
                    )
                }
                visible={viewMode === PdfViewerViewModeFlag.Annotation}
            />,
            <HeaderActionItem
                key="print"
                isTablet={isTablet}
                title={i18next.t('header.print')}
                onClick={() => ApryseSDK.showPrintDialog()}
                onClose={handleCloseHeaderMenu}
                icon={<Print />}
                visible={ReduxUpdater.viewMode !== PdfViewerViewModeFlag.Error}
            />,
            <HeaderActionItem
                key="undoButton"
                isTablet={isTablet}
                disabled={!canUndo}
                title={i18next.t('command.title.undo')}
                onClick={ApryseSDK.undo}
                icon={<Undo />}
                visible={
                    ![
                        PdfViewerViewModeFlag.Error,
                        PdfViewerViewModeFlag.EmailsAndOffice,
                        PdfViewerViewModeFlag.Images,
                    ].includes(viewMode)
                }
            />,
            <HeaderActionItem
                key="redoButton"
                isTablet={isTablet}
                disabled={!canRedo}
                title={i18next.t('command.title.redo')}
                onClick={ApryseSDK.redo}
                icon={<Redo />}
                visible={
                    ![
                        PdfViewerViewModeFlag.Error,
                        PdfViewerViewModeFlag.EmailsAndOffice,
                        PdfViewerViewModeFlag.Images,
                    ].includes(viewMode)
                }
            />,
            ...[
                isTablet && [
                    <Divider key="divider-settings" />,
                    <HeaderActionItem
                        key="information"
                        isTablet={isTablet}
                        title={i18next.t('header.information')}
                        onClick={() => {
                            return; // ToDO: GlobalDialogs.showDialog(i18next.t('aboutDialog.title'), <InfoDialog />)
                        }}
                        onClose={handleCloseHeaderMenu}
                        icon={<InfoOutlined />}
                    />,
                    <Divider key="divider-testing" />,
                ],
            ],
        ],
        [
            viewMode,
            refreshRibbon,
            refreshUndoRedo,
            canUndo,
            canRedo,
            isTablet,
            isDocumentSaving,
            isDocumentSaved,
        ],
    );

    /** Action items for desktop and tablet mode. */
    const commonDevOnlyItems =
        GlobalVariables.isForTestRun && ReduxUpdater.viewMode !== PdfViewerViewModeFlag.Error
            ? [
                  <HeaderActionItem
                      key="loadingModeButton"
                      buttonId="loadingModeButton"
                      isTablet={isTablet}
                      title="Loading"
                      onClick={() => (ReduxUpdater.viewMode = PdfViewerViewModeFlag.Loading)}
                      onClose={handleCloseHeaderMenu}
                  />,
                  <HeaderActionItem
                      key="viewModeButton"
                      buttonId="viewModeButton"
                      isTablet={isTablet}
                      title="View"
                      onClick={() => (ReduxUpdater.viewMode = PdfViewerViewModeFlag.View)}
                      onClose={handleCloseHeaderMenu}
                  />,
                  <HeaderActionItem
                      key="editModeButton"
                      buttonId="editModeButton"
                      isTablet={isTablet}
                      title="Edit"
                      onClick={() => (ReduxUpdater.viewMode = PdfViewerViewModeFlag.Annotation)}
                      onClose={handleCloseHeaderMenu}
                  />,
                  <HeaderActionItem
                      key="officeModeButton"
                      buttonId="officeModeButton"
                      isTablet={isTablet}
                      title="Emails and Office"
                      onClick={() =>
                          (ReduxUpdater.viewMode = PdfViewerViewModeFlag.EmailsAndOffice)
                      }
                      onClose={handleCloseHeaderMenu}
                  />,
                  <HeaderActionItem
                      key="compareModeButton"
                      buttonId="compareModeButton"
                      isTablet={isTablet}
                      title="Compare Documents"
                      onClick={() => (ReduxUpdater.viewMode = PdfViewerViewModeFlag.Compare)}
                      onClose={handleCloseHeaderMenu}
                  />,
                  <HeaderActionItem
                      key="imagesModeButton"
                      buttonId="imagesModeButton"
                      isTablet={isTablet}
                      title="Image Documents"
                      onClick={() => (ReduxUpdater.viewMode = PdfViewerViewModeFlag.Images)}
                      onClose={handleCloseHeaderMenu}
                  />,
              ]
            : [];

    /**
     * Close header menu on resize window.
     */
    React.useEffect(() => {
        handleCloseHeaderMenu();
    }, [isTablet]);

    return isTablet ? (
        <Stack className={styles.menuGroup} direction="row" spacing={1} pl={3} pr={2}>
            <ViewModeChip />
            <IconButton className={styles.controlBtn} onClick={handleOpenHeaderMenu}>
                <MoreVert />
            </IconButton>
            <Menu
                anchorEl={anchorHeaderMenu}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                open={Boolean(anchorHeaderMenu)}
                onClose={handleCloseHeaderMenu}
            >
                <MenuItem key="autoSave" disableRipple sx={{ pl: '8px' }}>
                    {/*autosaveSwitch*/}
                    {i18next.t('header.autoSave')}
                </MenuItem>
                {commonMenuItems}
            </Menu>
        </Stack>
    ) : (
        <Stack className={styles.menuGroup} direction="row" spacing={1} pl={3} pr={2}>
            <ViewModeChip />
            {/*autosaveSwitch*/}
            {commonMenuItems}
            {commonDevOnlyItems}
        </Stack>
    );
};
